import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/i18n/LanguageContext";

const links = [
  { to: "/", fr: "Manifeste", en: "Manifesto" },
  { to: "/recit", fr: "Le Récit", en: "The Story" },
  { to: "/riquet", fr: "Riquet", en: "Riquet" },
  { to: "/artistes", fr: "Je postule", en: "Apply" },
  { to: "/parcours", fr: "Parcours", en: "The Trail" },
  { to: "/carnet", fr: "Le Carnet", en: "Journal" },
  { to: "/boutique", fr: "La Boutique", en: "The Shop" },
  { to: "/partenaires", fr: "Partenaires", en: "Partners" },
  { to: "/mecenat", fr: "Mécénat", en: "Patronage" },
  { to: "/rejoindre", fr: "Je participe", en: "Support Us" },
  { to: "/contact", fr: "Contact", en: "Contact" },
];

function LangToggle({ className = "" }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 font-mono text-[11px] tracking-[0.2em] uppercase ${className}`} data-testid="lang-toggle">
      <button
        type="button"
        onClick={() => setLang("fr")}
        className={`px-1.5 py-1 transition-colors ${lang === "fr" ? "text-cinabre font-bold" : "text-fg/50 hover:text-fg"}`}
        data-testid="lang-fr"
        aria-label="Français"
      >
        FR
      </button>
      <span className="text-fg/30">/</span>
      <button
        type="button"
        onClick={() => setLang("en")}
        className={`px-1.5 py-1 transition-colors ${lang === "en" ? "text-cinabre font-bold" : "text-fg/50 hover:text-fg"}`}
        data-testid="lang-en"
        aria-label="English"
      >
        EN
      </button>
    </div>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth() || {};
  const { lang } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.1, delay: 1.5, ease: [0.65, 0, 0.35, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-bg/95 backdrop-blur-xl border-b border-fg/10 shadow-[0_1px_20px_-8px_rgba(0,0,0,0.15)]"
          : "bg-bg/70 backdrop-blur-md border-b border-fg/[0.06]"
      }`}
      data-testid="site-nav"
    >
      <div className="flex items-center justify-between px-6 md:px-12 h-20">
        <Link to="/" className="flex items-baseline gap-3" data-testid="nav-logo">
          <span className="font-serif text-2xl tracking-tight leading-none">Le Fil du Midi</span>
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/75 hidden sm:inline">FILM · 2026</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `link-underline font-mono text-[11px] tracking-[0.2em] uppercase font-medium ${isActive ? "text-cinabre" : "text-fg hover:text-cinabre"}`
              }
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
            >
              {l[lang]}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangToggle />
          {user ? (
            <Link to={user.role === "admin" ? "/admin" : "/espace"} className="btn-editorial" data-testid="nav-account">
              {user.role === "admin" ? "Back-office" : lang === "en" ? "My space" : "Mon espace"}
            </Link>
          ) : (
            <Link to="/login" className="btn-editorial ghost" data-testid="nav-login">
              {lang === "en" ? "Sign in" : "Se connecter"}
            </Link>
          )}
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            className="lg:hidden overflow-hidden bg-bg border-t border-fg/10"
          >
            <div className="flex flex-col py-6 px-6 gap-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className="font-serif text-2xl"
                  data-testid={`mobile-link-${l.to.replace("/", "") || "home"}`}
                >
                  {l[lang]}
                </NavLink>
              ))}
              <div className="flex items-center justify-between gap-4 mt-4 flex-wrap">
                <Link to={user ? (user.role === "admin" ? "/admin" : "/espace") : "/login"} className="btn-editorial w-fit" onClick={() => setOpen(false)}>
                  {user ? (user.role === "admin" ? "Back-office" : lang === "en" ? "My space" : "Mon espace") : lang === "en" ? "Sign in" : "Se connecter"}
                </Link>
                <LangToggle />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
